'use client';

import { useEffect, useRef } from 'react';

/**
 * The public menu's row-interaction contract.
 *
 * WHY THIS EXISTS. Most published menus (72 of 81 on 2026-09-18) render their
 * body through the template engine, not through `PlaceMenuList`. A template is
 * a merchant's design: it emits whatever nodes the designer laid out, and the
 * repeat root for a dish carries nothing but `data-item-id`. So the one thing
 * Menuraq must guarantee about that node — that a guest can tell it is tappable
 * and can actually tap it — cannot live in the template. It lives here.
 *
 * WHAT WAS WRONG. Rows were already being given `role="button"`, a tab stop and
 * an accessible name by a MutationObserver inside
 * `PublicMenuOrderingExperience`. Two things were still broken, and doc 09 §0.4
 * saw the symptom without the cause:
 *
 *   1. That observer lived inside the ordering island, which only mounts once a
 *      fulfilment mode is chosen. On a browse-only menu the rows were announced
 *      as nothing and could not be reached by keyboard at all.
 *   2. Nothing about a decorated row *looked* interactive: computed `cursor` was
 *      `auto` and `outline-style` was `none`, so there was no pointer affordance
 *      and no visible focus indicator (WCAG 2.4.7). That absence is exactly what
 *      the first-run hint — "Tap an item to see it, then add it to your order" —
 *      was there to say in words. Doc 04 §6 law 7: a surface that needs to
 *      explain itself has a defect, and this was the defect.
 *
 * SO THE CONTRACT IS: every `[data-item-id]` under the menu body is announced as
 * a button, is a tab stop, has a name, and carries `data-mnrq-row="item"`. That
 * attribute is the hook the stylesheet in `app/globals.css` uses to paint the
 * pointer cursor, the focus ring and the chevron. Attribute-only, because the
 * alternative — injecting a child node into a merchant's card — would break
 * layouts we do not own.
 *
 * Mounted by the page rather than by the ordering island, so it is true in every
 * customer mode.
 */

/**
 * The menu body — whichever engine painted it. Chrome (header, nav, action bar,
 * sheets) sits outside this and is never decorated or delegated to.
 */
export const MENU_BODY_SELECTOR = '[data-menu-item-click-root="true"]';

/** Marks a node this layer has taken responsibility for. Styled in globals.css. */
export const ORDERABLE_ROW_ATTR = 'data-mnrq-row';

export type OrderableRowLabels = {
  /** Accessible name for a row, by item id. Usually the dish's title. */
  get(itemId: string): string | undefined;
};

export function useOrderableMenuRows({
  rootSelector,
  labels,
  enabled = true,
}: {
  /** The menu body. Chrome outside it must never be decorated. */
  rootSelector: string;
  labels: OrderableRowLabels;
  enabled?: boolean;
}) {
  /* ── Give the row its focus back when the sheet closes ──
   *
   * Opening a dish from the keyboard and closing it again dropped focus on the
   * BRANCH TITLE, at the very top of the page. Measured: a guest who added an
   * item then wanted the cart needed **39 tab presses** to reach "Review
   * order", when from the row they were standing on it is 11.
   *
   * The cause is that the row is not a Radix trigger — it is a template node
   * this layer decorated — so the dialog has nothing recorded to hand focus
   * back to and the shell falls back to the first focusable on the page. That
   * is a WCAG 2.4.3 failure, and it is this layer's to fix, because this layer
   * is the reason the row is focusable in the first place.
   *
   * The restore is deliberately narrow. It only fires when the element that
   * received focus sits BEFORE the menu body in document order — i.e. the shell
   * fell back to header chrome. Anything the app focused on purpose inside the
   * body, or in the action bar below it, is left exactly where it is.
   */
  const lastRowRef = useRef<HTMLElement | null>(null);
  const generatedLabels = useRef(new WeakMap<HTMLElement, string>());

  useEffect(() => {
    if (!enabled) return;

    const remember = (event: Event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      const row = target.closest<HTMLElement>(`[${ORDERABLE_ROW_ATTR}="item"]`);
      if (row) lastRowRef.current = row;
    };
    document.addEventListener('click', remember, true);
    document.addEventListener('keydown', remember, true);

    const restoreIfShellFellBack = () => {
      const row = lastRowRef.current;
      const body = document.querySelector(rootSelector);
      if (!row || !row.isConnected || !body) return;
      const active = document.activeElement;
      if (!active || active === document.body || active === document.documentElement) {
        row.focus();
        return;
      }
      if (!(active instanceof HTMLElement) || body.contains(active)) return;
      // DOCUMENT_POSITION_PRECEDING: focus landed above the menu — the header.
      const precedesBody = body.compareDocumentPosition(active) & Node.DOCUMENT_POSITION_PRECEDING;
      if (precedesBody) row.focus();
    };

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.removedNodes) {
          if (node.nodeType !== 1) continue;
          const el = node as Element;
          if (el.matches?.('[role="dialog"]') || el.querySelector?.('[role="dialog"]')) {
            // After the shell has had its turn, not during it.
            requestAnimationFrame(() => requestAnimationFrame(restoreIfShellFellBack));
            return;
          }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      document.removeEventListener('click', remember, true);
      document.removeEventListener('keydown', remember, true);
      observer.disconnect();
    };
  }, [rootSelector, enabled]);

  useEffect(() => {
    if (!enabled) return;

    const decorate = (root: Element) => {
      root.querySelectorAll<HTMLElement>('[data-item-id]').forEach((el) => {
        // A template that already ships a real control keeps it — this layer
        // fills gaps, it does not overwrite intent.
        const tag = el.tagName.toLowerCase();
        if (tag === 'button' || tag === 'a') return;

        if (!el.hasAttribute('role')) el.setAttribute('role', 'button');
        if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '0');
        const currentLabel = el.getAttribute('aria-label');
        const previousGeneratedLabel = generatedLabels.current.get(el);
        if (!currentLabel || currentLabel === previousGeneratedLabel) {
          const title = labels.get(el.dataset.itemId?.trim() || '');
          if (title) {
            const nextLabel = `${title}. Open item details.`;
            if (currentLabel !== nextLabel) el.setAttribute('aria-label', nextLabel);
            generatedLabels.current.set(el, nextLabel);
          } else if (previousGeneratedLabel) {
            el.removeAttribute('aria-label');
            generatedLabels.current.delete(el);
          }
        }
        if (!el.hasAttribute(ORDERABLE_ROW_ATTR)) {
          el.setAttribute(ORDERABLE_ROW_ATTR, 'item');
        }
      });
    };

    const run = () => {
      const root = document.querySelector(rootSelector);
      if (root) decorate(root);
    };

    run();

    const root = document.querySelector(rootSelector);
    if (!root) return;
    // The body re-renders on category switches, search and live menu updates,
    // so the contract has to be re-applied rather than applied once.
    const observer = new MutationObserver(run);
    observer.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-item-id'] });
    return () => observer.disconnect();
  }, [rootSelector, labels, enabled]);
}
